import { DEFAULT_SETTINGS } from './settingsStore';
import type { NotificationApi } from './notificationsAdapter';
import type { NotificationItem, NotificationKind } from './types';

const MINUTE = 60_000;

interface SeedRow {
  kind: NotificationKind;
  title: string;
  body: string;
  actor: string;
  scope: string;
  agoMin: number;
  unread: boolean;
  link: string;
}

const SEED: readonly SeedRow[] = [
  { kind: 'message', title: 'Serena Davis', body: "Hey team, I've shared the latest design mockups in the channel.", actor: 'u-serena', scope: 'Product Design', agoMin: 0, unread: true, link: '/chat/c-serena' },
  { kind: 'message', title: 'Babak Shammas', body: "I've added the meeting notes for today.", actor: 'u-babak', scope: 'General', agoMin: 2, unread: true, link: '/chat/c-babak' },
  { kind: 'message', title: 'Audi Kapoor', body: 'Can we reschedule the review to 4 PM?', actor: 'u-audi', scope: 'General', agoMin: 10, unread: true, link: '/chat/c-audi' },
  { kind: 'mention', title: 'Marco Silva mentioned you', body: '@you can you sign off on the release notes?', actor: 'u-marco', scope: 'DevOps Squad', agoMin: 26, unread: true, link: '/teams/t-dev/c-release' },
  { kind: 'meetingReminder', title: 'Design review starts in 10 minutes', body: '11:00 AM – 11:45 AM', actor: 'u-lena', scope: 'Calendar', agoMin: 34, unread: false, link: '/calendar' },
  { kind: 'callMissed', title: 'Missed call from Charlotte De Crum', body: 'Product Lead', actor: 'u-charlotte', scope: 'Calls', agoMin: 55, unread: false, link: '/calls' },
  { kind: 'reaction', title: 'Lena Fischer reacted 🎉', body: 'to your post in Product Design', actor: 'u-lena', scope: 'Product Design', agoMin: 140, unread: false, link: '/teams/t-pd/c-general' },
  { kind: 'fileShared', title: 'Tom Becker shared Q3-roadmap.pdf', body: '2.4 MB · Product Design', actor: 'u-tom', scope: 'Files', agoMin: 220, unread: false, link: '/files' },
  { kind: 'taskAssigned', title: 'Task assigned: Ship presence service', body: 'Due tomorrow', actor: 'u-babak', scope: 'Tasks', agoMin: 300, unread: false, link: '/tasks' },
];

let counter = 0;

function toItem(row: SeedRow, now: number): NotificationItem {
  counter += 1;
  return {
    id: `n-${counter}`,
    kind: row.kind,
    priority: row.kind === 'mention' ? 'high' : 'normal',
    actorId: row.actor,
    scopeKind: row.kind === 'message' ? 'conversation' : 'channel',
    scopeId: row.scope.toLowerCase().replace(/\s+/g, '-'),
    deepLink: row.link,
    title: row.title,
    body: row.body,
    preview: { actorName: row.title.split(' mentioned')[0] ?? row.title, scopeName: row.scope, snippet: row.body },
    groupCount: 1,
    isRead: !row.unread,
    isSeen: !row.unread,
    createdAt: now - row.agoMin * MINUTE,
  };
}

const listeners = new Set<(item: NotificationItem, delivery: null) => void>();

/** Injection point used by the demo stream and by tests. */
export function emitMockNotification(item: NotificationItem): void {
  for (const listener of listeners) listener(item, null);
}

export function buildMockNotification(index: number): NotificationItem {
  const row = SEED[index % SEED.length];
  if (!row) throw new Error('Mock seed is empty');
  return toItem(row, Date.now());
}

export const notificationsMock: NotificationApi = {
  async list({ limit = 50 }) {
    const now = Date.now();
    const items = SEED.slice(0, limit).map((row) => toItem(row, now));
    return { items, nextCursor: null };
  },
  async markRead() {},
  async markAllSeen() {},
  async markAllRead() {},
  async dismiss() {},
  async getSettings() {
    return DEFAULT_SETTINGS;
  },
  async saveSettings() {},
  /* The mock has no server, so the ledger is whatever was asked for. */
  async setMute(input) {
    return input.minutes === 0
      ? []
      : [{
          scopeKind: input.scopeKind,
          scopeId: input.scopeId,
          scopeName: 'Mock scope',
          until: input.minutes === null ? null : Date.now() + input.minutes * MINUTE,
          allowMentions: input.allowMentions ?? false,
        }];
  },
  async saveRule(input) {
    return {
      id: input.id ?? `rule-${Date.now().toString(36)}`,
      name: input.name,
      enabled: input.enabled ?? true,
      priority: 100,
      match: { keywords: [...input.keywords] },
      action: { level: 'all', channels: ['inapp', 'push'], priority: input.urgent === true ? 'urgent' : 'high' },
    };
  },
  async deleteRule() {},
  subscribe(onNotification) {
    listeners.add(onNotification);
    return () => {
      listeners.delete(onNotification);
    };
  },

  /* The seeded feed never withdraws anything — there is no server to change its
     mind. A no-op rather than an omission, so the mock still satisfies the
     whole contract and a consumer never has to feature-test the adapter. */
  subscribeDismissals() {
    return () => undefined;
  },
};
